import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getExecutiveSummary } from '../../../services/executiveAnalytics';
import { toScopeQuery } from '../../../utils/scopeSelection';
import { CONTROL_TABLE, toTableQuery } from '../../../utils/analyticsTableParams';

const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

/*
 * ТЗ V5 №1 + №19: «Контроль» считает только применённый период.
 *
 * Один GET /analytics/executive-summary несёт и карточки, и таблицу «Камеры и
 * простои»: период + scope + control_*. Раньше вкладка слала 1/7/30 дней
 * параллельно и отдельный запрос таблицы — отсюда ошибки загрузки и медленный
 * вход.
 *
 * Одинаковые запросы склеиваются (inFlight по ключу параметров): StrictMode
 * монтирует эффект дважды, и второй монтаж подхватывает уже летящий запрос, а
 * не шлёт новый. Отменяется запрос, только когда от него отписались все — с
 * отложенной проверкой, чтобы unmount+mount одного тика его не убил.
 * Устаревший ответ отбрасывается по seq: побеждает последний применённый выбор.
 */
const inFlight = new Map();

const acquire = (key, params) => {
  let entry = inFlight.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = { controller, users: 0, promise: null };
    const current = entry;
    current.promise = getExecutiveSummary(params, { signal: controller.signal })
      .finally(() => { if (inFlight.get(key) === current) inFlight.delete(key); });
    inFlight.set(key, current);
  }
  entry.users += 1;
  return entry;
};

const release = (key, entry) => {
  entry.users -= 1;
  setTimeout(() => {
    if (entry.users > 0) return;
    entry.controller.abort();
    if (inFlight.get(key) === entry) inFlight.delete(key);
  }, 0);
};

export const resetControlSummaryForTests = () => inFlight.clear();

/** Параметры запроса «Контроля»: те же уходят в control-timeline (№11). */
export const buildControlRequest = ({ range, scope, tableState }) => ({
  from: range?.from || '',
  to: range?.to || '',
  ...toScopeQuery(scope),
  ...toTableQuery(CONTROL_TABLE, tableState),
});

export const useControlSummary = ({ range, scope, tableState, enabled = true }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [reloadToken, setReloadToken] = useState(0);
  const seqRef = useRef(0);

  const requestKey = useMemo(
    () => JSON.stringify(buildControlRequest({ range, scope, tableState })),
    [range, scope, tableState]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const params = JSON.parse(requestKey);
    if (!params.from || !params.to) return undefined;

    const seq = ++seqRef.current;
    // «Повторить» — тот же период, но новый запрос: токен входит только в ключ склейки.
    const key = `${requestKey}#${reloadToken}`;
    const entry = acquire(key, params);
    setStatus('loading');

    entry.promise
      .then((response) => {
        if (seq !== seqRef.current) return;
        setData(response);
        setStatus('ready');
      })
      .catch((error) => {
        if (seq !== seqRef.current || isCanceled(error)) return;
        setStatus('error');
      });

    return () => release(key, entry);
  }, [enabled, requestKey, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { data, status, reload };
};

export default useControlSummary;
